"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useSimpleTableContext } from "@/components/shared/UniversalCRMTable/contexts/SimpleTableContext";
import { renderFieldInput, convertFieldValue } from "@/components/shared/UniversalCRMTable/utils/unified-field-renderer";
import { getBulkEditableFields } from "./utils/fieldBasedBulkActions";
import type { BaseEntity, FieldValue } from "@/components/shared/UniversalCRMTable/types";

interface GenericBulkEditModalProps<T extends BaseEntity> {
  selectedItems?: T[];
  isOpen: boolean;
  onClose: () => void;
  clearSelection?: () => void;
}

/**
 * Generic bulk edit modal that renders form fields from config.
 * Uses config and hooks from SimpleTableContext - works for any entity
 * that has bulkEditable fields and useUpdate.
 */
export function GenericBulkEditModal<T extends BaseEntity>({
  selectedItems = [],
  isOpen,
  onClose,
  clearSelection,
}: GenericBulkEditModalProps<T>) {
  const { config, hooks, onOpenDetailModal } = useSimpleTableContext<T>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const bulkEditableFields = useMemo(
    () =>
      getBulkEditableFields(config).map(({ fieldId, fieldConfig }) => ({
        fieldId,
        label: fieldConfig.label ?? fieldId,
        fieldConfig,
      })),
    [config]
  );
  const items = selectedItems;
  const item = items[currentIndex] ?? null;

  const updateMutation = hooks?.useUpdate?.();

  const syncFormFromItem = useCallback(
    (entity: T) => {
      const next: Record<string, unknown> = {};
      for (const { fieldId } of bulkEditableFields) {
        const val = (entity as Record<string, unknown>)[fieldId];
        next[fieldId] = val ?? "";
      }
      setFormData(next);
      setErrors({});
    },
    [bulkEditableFields]
  );

  useEffect(() => {
    if (item && isOpen) {
      syncFormFromItem(item);
    }
  }, [item, isOpen, syncFormFromItem]);

  const handleFieldChange = useCallback((fieldId: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item || !updateMutation) return;

    const updates: Record<string, unknown> = { id: item.id };
    for (const { fieldId, fieldConfig } of bulkEditableFields) {
      const raw = formData[fieldId];
      updates[fieldId] = convertFieldValue(raw as string, fieldConfig);
    }

    try {
      await updateMutation.mutateAsync(updates as Partial<T> & { id: string });
      if (currentIndex < items.length - 1) {
        setCurrentIndex((i) => i + 1);
      }
    } catch (err) {
      console.error("GenericBulkEditModal save failed:", err);
    }
  };

  const handleClose = () => {
    clearSelection?.();
    onClose();
  };

  if (!item) return null;
  if (!updateMutation) return null;

  const displayName =
    (item as Record<string, unknown>).title ||
    (item as Record<string, unknown>).name ||
    (item as Record<string, unknown>).email ||
    `Item ${currentIndex + 1}`;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === items.length - 1;

  const footerActions = (
    <div className="flex items-center justify-between w-full gap-4 flex-wrap pt-4 border-t mt-6">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
          disabled={isFirst}
          className="gap-1"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">
          {currentIndex + 1} of {items.length}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setCurrentIndex((i) => Math.min(items.length - 1, i + 1))}
          disabled={isLast}
          className="gap-1"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex items-center gap-2">
        {onOpenDetailModal && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onOpenDetailModal(item, items.map((i) => i.id), currentIndex);
              onClose();
            }}
            className="gap-1"
          >
            <ExternalLink className="h-4 w-4" />
            View Details
          </Button>
        )}
        <Button type="button" variant="outline" onClick={handleClose} disabled={updateMutation.isPending}>
          Close
        </Button>
        <Button
          type="submit"
          form="generic-bulk-edit-form"
          disabled={updateMutation.isPending}
          className="min-w-[100px]"
        >
          {updateMutation.isPending ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      title={`Edit ${String(displayName)}`}
      description={`Item ${currentIndex + 1} of ${items.length}`}
      maxWidth="2xl"
      side="bottom"
    >
      <form id="generic-bulk-edit-form" onSubmit={handleSave} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {bulkEditableFields.map(({ fieldId, label, fieldConfig }) => (
            <div key={fieldId}>
              <Label htmlFor={fieldId}>{label}</Label>
              <div className="mt-1">
                {renderFieldInput({
                  fieldId,
                  fieldConfig,
                  value: (formData[fieldId] ?? (item as Record<string, unknown>)[fieldId]) as FieldValue,
                  onValueChange: (v) => handleFieldChange(fieldId, v),
                  placeholder: fieldConfig.placeholder,
                })}
              </div>
              {errors[fieldId] && (
                <p className="text-sm text-red-500 mt-1">{errors[fieldId]}</p>
              )}
            </div>
          ))}
        </div>
      </form>
      {footerActions}
    </ResponsiveModal>
  );
}
